<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <button
                  class="btn btn-outline-secondary btn-sm mb-0 me-3"
                  @click="$router.push(`/products/${productId}`)"
                >
                  <i class="fas fa-arrow-left me-1"></i> 返回
                </button>
                <h6 class="mb-0">庫存管理</h6>
              </div>
            </div>
          </div>
          <div class="card-body">
            <!-- Product Info -->
            <div class="mb-4" v-if="product.name">
              <h5>{{ product.name }}</h5>
              <p class="text-sm text-muted mb-0">
                商品編號: {{ product.product_code }}
              </p>
            </div>

            <!-- Main Product Inventory (if no variants) -->
            <div v-if="variants.length === 0" class="card mb-3">
              <div class="card-body">
                <div class="row align-items-center">
                  <div class="col-md-6">
                    <h6 class="mb-2">主商品</h6>
                    <p class="text-sm mb-1">
                      <strong>價格:</strong> NT$ {{ product.price }}
                    </p>
                    <p class="text-sm mb-1">
                      <strong>庫存:</strong> {{ product.stock }}
                    </p>
                    <p class="text-sm mb-0">
                      <strong>實體庫存:</strong>
                      {{ product.physical_stock || 0 }}
                    </p>
                  </div>
                  <div class="col-md-6 text-end">
                    <button
                      v-if="can('product.inventory')"
                      class="btn btn-primary"
                      @click="openInboundModal(null)"
                    >
                      <i class="fas fa-plus me-1"></i> 入庫
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Variants List -->
            <div v-if="variants.length > 0">
              <h6 class="mb-3">選擇 SKU 進行入庫</h6>
              <div class="row">
                <div
                  v-for="variant in variants"
                  :key="variant.id"
                  class="col-md-6 mb-3"
                >
                  <div class="card h-100">
                    <div class="card-body">
                      <div class="d-flex justify-content-between mb-2">
                        <h6 class="mb-0">
                          {{
                            variant.display_name || formatVariantSpecs(variant)
                          }}
                        </h6>
                        <soft-badge
                          :color="
                            variant.status === 'active'
                              ? 'success'
                              : 'secondary'
                          "
                          variant="gradient"
                          size="sm"
                        >
                          {{ variant.status === "active" ? "上架" : "下架" }}
                        </soft-badge>
                      </div>
                      <p class="text-xs text-muted mb-2">
                        SKU: {{ variant.variant_code }}
                      </p>
                      <div class="row mb-3">
                        <div class="col-6">
                          <p class="text-sm mb-1">
                            <strong>價格:</strong> NT$ {{ variant.price }}
                          </p>
                          <p class="text-sm mb-0">
                            <strong>庫存:</strong> {{ variant.stock }}
                          </p>
                        </div>
                        <div class="col-6">
                          <p class="text-sm mb-0">
                            <strong>實體庫存:</strong>
                            {{ variant.physical_stock || 0 }}
                          </p>
                        </div>
                      </div>
                      <button
                        v-if="can('product.inventory')"
                        class="btn btn-primary btn-sm w-100"
                        @click="openInboundModal(variant.id)"
                      >
                        <i class="fas fa-plus me-1"></i> 入庫此 SKU
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Warehouse Allocation Section -->
    <div class="card mt-4" v-if="can('warehouses.manage')">
      <div class="card-header pb-0 d-flex justify-content-between align-items-center">
        <h6 class="mb-0"><i class="fas fa-warehouse me-2 text-muted"></i>倉庫分配</h6>
        <router-link to="/warehouses" class="btn btn-outline-secondary btn-sm">
          管理倉庫
        </router-link>
      </div>
      <div class="card-body">
        <div v-if="warehouseLoading" class="text-center py-3">
          <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
        </div>
        <div v-else-if="warehouseAllocations.length === 0" class="text-muted text-sm text-center py-3">
          此商品尚未分配到任何倉庫。請前往
          <router-link to="/warehouses">倉庫管理</router-link>
          進行分配。
        </div>
        <div v-else>
          <!-- Group by variant -->
          <div
            v-for="group in allocationGroups"
            :key="group.variantId ?? 'base'"
            class="mb-3"
          >
            <div class="text-xs text-uppercase text-muted fw-bold mb-2">
              <span v-if="group.variantId">
                規格 {{ group.variantCode }}
                <span v-if="group.variantName"> · {{ group.variantName }}</span>
              </span>
              <span v-else>主商品</span>
              <span class="ms-2 text-dark">（總庫存 {{ group.totalStock }}）</span>
            </div>
            <div class="row g-2">
              <div
                v-for="alloc in group.allocations"
                :key="alloc.id"
                class="col-md-4 col-6"
              >
                <div class="p-2 border rounded d-flex justify-content-between align-items-center">
                  <div>
                    <div class="text-sm fw-bold">{{ alloc.warehouse_name }}</div>
                    <div class="text-xs text-muted">
                      <span class="badge badge-sm" :class="alloc.warehouse_type === 'online' ? 'bg-info' : 'bg-warning text-dark'">
                        {{ alloc.warehouse_type === 'online' ? '線上' : '門市' }}
                      </span>
                      {{ alloc.warehouse_code }}
                    </div>
                  </div>
                  <div class="text-end">
                    <div class="h6 mb-0 text-primary">{{ alloc.allocated_stock }}</div>
                    <div class="text-xs text-muted">分配</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="text-xs text-muted mt-1">
              已分配: <strong>{{ group.totalAllocated }}</strong> ／
              未分配: <strong :class="group.unallocated > 0 ? 'text-success' : 'text-muted'">{{ group.unallocated }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Inbound Modal -->
    <inbound-modal
      :visible="showInbound"
      :product-id="selectedProductId"
      :variant-id="selectedVariantId"
      @close="closeInboundModal"
      @success="handleSuccess"
      @error="handleError"
    />
  </div>
</template>

<script>
import SoftBadge from "@/components/SoftBadge.vue";
import InboundModal from "@/components/InboundModal.vue";
import request from "@/utils/request";
import { hasPermission, getCurrentUser } from "@/utils/permissions";

export default {
  name: "ProductInventory",
  components: {
    SoftBadge,
    InboundModal,
  },
  data() {
    return {
      productId: null,
      product: {},
      variants: [],
      showInbound: false,
      selectedProductId: null,
      selectedVariantId: null,
      warehouseAllocations: [],
      warehouseLoading: false,
    };
  },
  computed: {
    allocationGroups() {
      // Group allocations by variant (null = base product)
      const groups = {};
      this.warehouseAllocations.forEach((alloc) => {
        const key = alloc.product_variant_id ?? "base";
        if (!groups[key]) {
          groups[key] = {
            variantId: alloc.product_variant_id,
            variantCode: alloc.variant_code,
            variantName: alloc.variant_name,
            totalStock: alloc.product_variant_id
              ? (this.variants.find((v) => v.id === alloc.product_variant_id)?.stock ?? 0)
              : (this.product.stock ?? 0),
            allocations: [],
          };
        }
        groups[key].allocations.push(alloc);
      });

      return Object.values(groups).map((g) => ({
        ...g,
        totalAllocated: g.allocations.reduce((s, a) => s + a.allocated_stock, 0),
        unallocated: g.totalStock - g.allocations.reduce((s, a) => s + a.allocated_stock, 0),
      }));
    },
  },
  async created() {
    this.productId = this.$route.params.id;
    await this.fetchProduct();
    await this.fetchVariants();
    if (this.can("warehouses.manage")) {
      await this.fetchWarehouseAllocations();
    }
  },
  methods: {
    can(permission) {
      return hasPermission(getCurrentUser(), permission);
    },
    async fetchProduct() {
      try {
        const response = await request.get(
          `/api/admin/products/${this.productId}`
        );
        this.product = response.data.data;
      } catch (error) {
        console.error("Failed to fetch product", error);
        this.$toast.error("載入商品詳情失敗。");
        this.$router.push("/products");
      }
    },
    async fetchVariants() {
      try {
        const response = await request.get(
          `/api/admin/products/${this.productId}/variants`
        );
        this.variants = response.data.data;
      } catch (error) {
        console.error("Failed to fetch variants", error);
      }
    },
    formatVariantSpecs(variant) {
      const parts = [];
      if (variant.shaft_flex) parts.push(variant.shaft_flex);
      if (variant.loft_angle) parts.push(variant.loft_angle + "°");
      if (variant.length) parts.push(variant.length);
      if (variant.hand) parts.push(variant.hand === "left" ? "左手" : "右手");
      return parts.join(" / ") || "無規格";
    },
    openInboundModal(variantId) {
      this.selectedProductId = this.productId;
      this.selectedVariantId = variantId;
      this.showInbound = true;
    },
    closeInboundModal() {
      this.showInbound = false;
      this.selectedProductId = null;
      this.selectedVariantId = null;
    },
    async handleSuccess(msg) {
      this.$toast.success(msg);
      // Refresh data
      await this.fetchProduct();
      await this.fetchVariants();
      if (this.can("warehouses.manage")) {
        await this.fetchWarehouseAllocations();
      }
      this.closeInboundModal();
    },
    handleError(msg) {
      this.$toast.error(msg);
    },
    async fetchWarehouseAllocations() {
      this.warehouseLoading = true;
      try {
        const res = await request.get(
          `/api/admin/products/${this.productId}/warehouse-inventory`
        );
        this.warehouseAllocations = res.data.data || [];
      } catch {
        this.warehouseAllocations = [];
      } finally {
        this.warehouseLoading = false;
      }
    },
  },
};
</script>
